import React from 'react';
import PropTypes, { string } from 'prop-types';
import { Helmet } from 'react-helmet';
import { getLanguageFromField } from '../../../utilities/sharedUtils';
import { TS } from '../../../utilities/urlConstants';
import { urlJoinSafe } from '../../../utilities';

const getCanonicalUrl = (customCanonical, opengraphUrl, siteUrl, postUrl) => {
  if (customCanonical) return customCanonical;
  else if (opengraphUrl.indexOf(siteUrl) !== -1) return opengraphUrl;
  else return urlJoinSafe(siteUrl, postUrl, TS);
};

const ArticleSEO = (props) => {
  const {
    seo,
    advancedSeo,
    postLocale,
    postUrl,
    siteUrl,
    authorName,
    translations,
    children,
  } = props;

  // Advanced Seo
  const { customCanonical, noindexPost } = advancedSeo;

  // General
  const { title, metaDesc } = seo;

  // Robots
  const { metaRobotsNoindex, metaRobotsNofollow } = seo;

  // Opengraph
  const {
    opengraphTitle,
    opengraphDescription,
    opengraphSiteName,
    opengraphPublisher,
    opengraphPublishedTime,
    opengraphModifiedTime,
    opengraphImage,
    opengraphUrl,
  } = seo;

  // Twitter
  const { twitterTitle, twitterDescription, twitterImage, readingTime } = seo;

  // Static meta tags
  const ogType = 'article';
  const twitterCard = 'summary_large_image';
  const twitterLabel1 = 'Written by';
  const twitterLabel2 = 'Estimated reading time';

  // noindex
  const siteNoindex = noindexPost ? 'noindex' : metaRobotsNoindex;

  const i18nLanguageName = getLanguageFromField('locale', postLocale).i18nName;

  return (
    <Helmet
      htmlAttributes={{
        lang: i18nLanguageName,
      }}
      meta={[
        { name: 'description', content: metaDesc },
        {
          name: 'robots',
          content: [siteNoindex, metaRobotsNofollow].join(', '),
        },
        { property: 'og:locale', content: postLocale },
        { property: 'og:type', content: ogType },
        { property: 'og:title', content: opengraphTitle },
        { property: 'og:description', content: opengraphDescription },
        {
          property: 'og:url',
          content: postUrl ? urlJoinSafe(siteUrl, postUrl, TS) : undefined,
        },
        { property: 'og:site_name', content: opengraphSiteName },
        { property: 'article:publisher', content: opengraphPublisher },
        { property: 'article:published_time', content: opengraphPublishedTime },
        { property: 'article:modified_time', content: opengraphModifiedTime },
        {
          property: 'og:image',
          content: opengraphImage?.sourceUrl,
        },
        { name: 'twitter:card', content: twitterCard },
        { name: 'twitter:title', content: twitterTitle },
        { name: 'twitter:description', content: twitterDescription },
        {
          name: 'twitter:image',
          content: twitterImage?.sourceUrl,
        },
        { name: 'twitter:label1', content: twitterLabel1 },
        { name: 'twitter:data1', content: authorName },
        { name: 'twitter:label2', content: twitterLabel2 },
        { name: 'twitter:data2', content: `${readingTime} minutes` },
      ]}
    >
      <title>{title}</title>
      <link
        rel="canonical"
        href={getCanonicalUrl(customCanonical, opengraphUrl, siteUrl, postUrl)}
      />
      <link
        rel="alternate"
        hrefLang={i18nLanguageName}
        href={urlJoinSafe(siteUrl, postUrl, TS)}
      />
      {translations?.map((translation) => {
        const languageData = getLanguageFromField(
          'locale',
          translation?.language?.locale
        );
        return (
          <link
            key={languageData.i18nName}
            rel="alternate"
            hrefLang={languageData.i18nName}
            href={urlJoinSafe(
              languageData.languageSiteUrl,
              translation?.slug ?? '',
              TS
            )}
          />
        );
      })}
      {children}
    </Helmet>
  );
};

ArticleSEO.defaultProps = {
  authorName: '',
  translations: [],
};

ArticleSEO.propTypes = {
  authorName: PropTypes.string,
  seo: PropTypes.object.isRequired,
  advancedSeo: PropTypes.object.isRequired,
  postLocale: PropTypes.string.isRequired,
  postUrl: PropTypes.string.isRequired,
  siteUrl: PropTypes.string.isRequired,
  translations: PropTypes.arrayOf(
    PropTypes.shape({
      language: PropTypes.shape({ locale: PropTypes.string }),
      slug: string,
    })
  ),
};

export default ArticleSEO;
